import * as Sentry from '@sentry/react';
import { Button, Col, DatePicker, DatePickerProps, Row } from 'antd';
import { FormDrawer } from 'components/common/Drawer/FormDrawer';
import { PageTitle } from 'components/common/PageTitle/PageTitle';
import { SliceStatus } from 'constants/enums/slices';
import { holidayFormItems } from 'constants/forms/holidays';
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks';
import { Holiday } from 'interfaces/holidays.interfaces';
import { useState } from 'react';
import { doListHolidays, doNewHoliday } from 'store/slices/holidaySlice';
import dayjs from 'dayjs';
import { useForm } from 'antd/es/form/Form';
import notificationHook from 'hooks/notificationHook';
import statusMessages from 'statusMessages';
import { ApiError } from 'types';
import { LoadingCard } from 'components/common/LoadingCard';
import { ErrorResult } from 'components/common/ErrorResult';
import { HolidaysList } from 'components/holidays/HolidaysList';

const currentYear = dayjs().year();

export const HolidaysPage = () => {
  const dispatch = useAppDispatch();
  const { holidayStatus, holidaysListStatus } = useAppSelector(
    (state) => state.holiday
  );
  const [openCreateDrawer, setOpenCreateDrawer] = useState(false);
  const [form] = useForm();
  const [year, setYear] = useState(dayjs(`${currentYear}`));

  if (holidaysListStatus === SliceStatus.Empty) {
    dispatch(doListHolidays());
  }

  const onNewHoliday = async (values: Holiday) => {
    const objectToStore = {
      _id: values._id,
      desc: values.desc,
      date: [
        dayjs(values.date[0]).endOf('day').toISOString(),
        dayjs(values.date[1]).endOf('day').toISOString(),
      ],
    };

    dispatch(doNewHoliday(objectToStore))
      .unwrap()
      .then(() => {
        notificationHook({
          type: 'success',
          message: 'Se ha creado el día festivo con éxito.',
        });
        form.resetFields();
        setOpenCreateDrawer(false);
      })
      .catch((error) => {
        const currentStatus = (error as ApiError).statusCode;
        notificationHook({
          message: statusMessages.holiday[
            currentStatus ? currentStatus : 500
          ] as string,
          type: 'error',
        });
        Sentry.captureException(error);
      });
  };

  const onYearChange: DatePickerProps['onChange'] = (date, dateString) => {
    if (date) {
      const selectedYear = dayjs(dateString as string);
      setYear(selectedYear);
      dispatch(doListHolidays(selectedYear.year()));
    }
  };

  const setCurrentYear = () => {
    const actualYear = dayjs(`${currentYear}`);
    setYear(actualYear);
    dispatch(doListHolidays(actualYear.year()));
  };

  return (
    <div id="holidays-page">
      <PageTitle>Días festivos</PageTitle>
      <Row>
        <Col xs={24}>
          <Row justify="space-between" align="middle">
            <Col>
              <h2 className="title" data-cy="holidays-title">
                Días festivos
              </h2>
            </Col>
            <Col xs={16}>
              <Row align="middle" gutter={16}>
                <Col xs={4}>
                  <DatePicker
                    picker="year"
                    value={year}
                    onChange={onYearChange}
                    data-cy="date-picker"
                  />
                </Col>
                <Col>
                  <Button
                    className="btn-link"
                    onClick={setCurrentYear}
                    data-cy="current-year-button"
                  >
                    Año actual
                  </Button>
                </Col>
              </Row>
            </Col>
            <Col xs={3}>
              <FormDrawer<Holiday>
                open={openCreateDrawer}
                textButton="Crear día festivo"
                title="Crear día festivo"
                width={500}
                confirmText="Crear"
                cancelText="Cancelar"
                formMode="create"
                formItems={holidayFormItems}
                loading={holidayStatus === SliceStatus.Updating}
                onConfirm={onNewHoliday}
                onOpen={() => {
                  setOpenCreateDrawer(true);
                }}
                onClose={() => {
                  setOpenCreateDrawer(false);
                }}
                form={form}
                dataCy="create-holiday"
              />
            </Col>
          </Row>
        </Col>
      </Row>
      {holidaysListStatus === SliceStatus.Updating && <LoadingCard />}
      {holidaysListStatus === SliceStatus.Fulfilled && (
        <HolidaysList setCurrentYear={setCurrentYear} />
      )}
      {holidaysListStatus === SliceStatus.Error && (
        <ErrorResult
          dataCy={'holidays-list-error'}
          title="No se pudo obtener la lista de días festivos"
        />
      )}
    </div>
  );
};
